/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type TriggerProps } from '@atlaskit/tooltip';

import { AgentStyles, PersonStyles, WorkStyles } from './styled';
import { KnowledgeCardsRowType } from './types';

const i18n = defineMessages({
	knowledgeRowTypeWork: {
		id: 'search_ai.knowledge_cards.row.knowledge-row-type.work',
		defaultMessage: 'Work Item',
		description:
			'Alt text that describes an icon representing a work item in the knowledge cards section generated by Rovo',
	},
	knowledgeRowTypeAgent: {
		id: 'search_ai.knowledge_cards.row.knowledge-row-type.agent',
		defaultMessage: 'Agent',
		description:
			'Alt text that describes an icon representing an agent in the knowledge cards section generated by Rovo',
	},
	knowledgeRowTypePerson: {
		id: 'search_ai.knowledge_cards.row.knowledge-row-type.person',
		defaultMessage: 'Person',
		description:
			'Alt text that describes an icon representing a person in the knowledge cards section generated by Rovo',
	},
});

const styles = cssMap({
	root: {
		marginInlineEnd: token('space.100'),
		borderRadius: token('radius.small'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			cursor: 'pointer',
		},
	},
});

type KnowledgeCardsRowProps = {
	primaryText: string;
	secondaryText: string;
	pictureUrl: string;
	type: KnowledgeCardsRowType;
	triggerProps?: TriggerProps;
	onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
};

export const KnowledgeCardsRow = ({
	primaryText,
	secondaryText,
	pictureUrl,
	type,
	triggerProps,
	onClick,
}: KnowledgeCardsRowProps) => {
	const intl = useIntl();
	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={styles.root}
			paddingInline="space.200"
			paddingBlock="space.100"
			{...triggerProps}
			onClick={onClick}
		>
			<Inline space="space.100" alignBlock="center">
				{type === KnowledgeCardsRowType.PERSON ? (
					<PersonStyles src={pictureUrl} alt={intl.formatMessage(i18n.knowledgeRowTypePerson)} />
				) : (
					<WorkStyles src={pictureUrl} alt={intl.formatMessage(i18n.knowledgeRowTypeWork)} />
				)}
				<Stack spread="space-between">
					<Text maxLines={1}>
						<Heading size="xxsmall">{primaryText}</Heading>
					</Text>
					<Text size="small" color="color.text.subtlest" maxLines={1}>
						{secondaryText}
					</Text>
				</Stack>
			</Inline>
		</Box>
	);
};

export const KnowledgeCardsRowRefresh = ({
	primaryText,
	secondaryText,
	pictureUrl,
	type,
	triggerProps,
	onClick,
}: KnowledgeCardsRowProps) => {
	const intl = useIntl();
	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
		<Box
			xcss={styles.root}
			paddingInline="space.200"
			paddingBlock="space.075"
			{...triggerProps}
			onClick={onClick}
		>
			<Inline space="space.100" alignBlock="center">
				{type === KnowledgeCardsRowType.WORK ? (
					<WorkStyles src={pictureUrl} alt={intl.formatMessage(i18n.knowledgeRowTypeWork)} />
				) : type === KnowledgeCardsRowType.AGENT ? (
					<AgentStyles src={pictureUrl} alt={intl.formatMessage(i18n.knowledgeRowTypeAgent)} />
				) : (
					<PersonStyles src={pictureUrl} alt={intl.formatMessage(i18n.knowledgeRowTypePerson)} />
				)}
				<Stack>
					<Text maxLines={1}>{primaryText}</Text>
					<Text size="small" color="color.text.subtlest" maxLines={1}>
						{secondaryText}
					</Text>
				</Stack>
			</Inline>
		</Box>
	);
};
